import { Box, Typography } from '@mui/material';
import { DataGridPro, gridClasses, GridLogicOperator } from '@mui/x-data-grid-pro';
import { deDE } from '@mui/x-data-grid-pro/locales';
import { useContext, useState } from 'react';
import { v4 as uuid4 } from 'uuid';

import { AccordionWithHeading, ContentLoading } from '../../../components/general';
import { CopyButton } from '../../../components/general/CopyButton.js';
import { AkaThemaId, taskStatus } from '../../../dtos.ts';
import { trpc } from '../../../trpc';
import { BackendConfigContext } from '../../../utils';
import { buildFilterItems, type CustomGridFilterModel, TaskFilter } from './TaskFilter';
import { taskCardContent } from './ThemaTaskCard.content';
import { taskColumns } from './ThemaTaskCard.utils';
import { ThemaTaskCardHeading } from './ThemaTaskCardHeading';

type ThemaTasksCardProps = {
  readonly akaThemaId: AkaThemaId;
};

export const ThemaTasksCard = (props: ThemaTasksCardProps) => {
  const { TaskManagementBoardUrl } = useContext(BackendConfigContext);
  const taskQuery = trpc.thema.getTasksByAkaThemaId.useQuery({ akaThemaId: props.akaThemaId });
  const [filterModel, setFilterModel] = useState<CustomGridFilterModel>({
    items: buildFilterItems([]),
    logicOperator: GridLogicOperator.Or,
  });

  if (!taskQuery.data) {
    let message = <ContentLoading />;
    if (taskQuery.isError) {
      console.warn(taskQuery.error.message);
      message = (
        <Typography variant="paragraph" color="error">
          {taskCardContent.jiraNotAvailable}
          <CopyButton text={taskQuery.error.message} />
        </Typography>
      );
    }

    return (
      <Box>
        <AccordionWithHeading key={uuid4()} heading={<ThemaTaskCardHeading uri={TaskManagementBoardUrl} />}>
          {message}
        </AccordionWithHeading>
      </Box>
    );
  }

  return (
    <Box>
      <AccordionWithHeading
        heading={<ThemaTaskCardHeading uri={TaskManagementBoardUrl} />}
        defaultExpanded={taskQuery.data.some((task) => task.taskStatus !== taskStatus.ABGESCHLOSSEN)}
      >
        <TaskFilter filterModel={filterModel} setFilterModel={setFilterModel} />
        <DataGridPro
          columns={taskColumns}
          rows={taskQuery.data}
          pagination
          pageSizeOptions={[5, 10]}
          filterModel={filterModel}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
            sorting: { sortModel: [{ sort: 'asc', field: 'taskStatus' }] },
          }}
          disableColumnMenu
          disableColumnFilter
          disableRowSelectionOnClick
          sx={{
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: 'none',
            },
            [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
              outline: 'none',
            },
          }}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        />
      </AccordionWithHeading>
    </Box>
  );
};
